import _, { toInteger } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { cancelLeaseApplication } from '../../../api/instant-lease-api';
import {
  fetchCarDetails,
  fetchLeaseApp,
} from '../../../redux/leaseApp/lease-app-slice';
import { EDevice } from '../../../redux/sequence/enums';
import { useAppDispatch } from '../../../redux/store';
import { RootState } from '../../../redux/types';
import { CarData, Type } from '../../../types/instant-lease-api';
import {
  canCancelLeaseApplication,
  formatGermanPrice,
  getIsAllowedToBeCanceled,
  getPartnerText,
} from '../../../utils/getter';
import { CapitalizeFirstLetter } from '../../../utils/helper';
import { ReactComponent as ArrowRight } from '../../b2b/components/icons/chevron-right.svg';
import { ReactComponent as ContractCancel20 } from '../../b2b/components/icons/contract_delete_20.svg';
import { ReactComponent as ContractCancel24 } from '../../b2b/components/icons/contract_delete_24.svg';
import UnknownCar from '../../b2b/components/images/unknown-car.png';
import ConfirmationModal from '../../b2c/components/confirmation-modal/confirmation-modal';
import LeaseRow from '../lease-row/leaseRow';
import {
  getFuelType,
  getGearBoxType,
  getVehicleBody,
  getVehicleType,
} from './car-data-mapper';

const CarDetails: React.FC<{
  showCancelButton?: boolean;
  carInfo?: CarData;
}> = ({ showCancelButton = true, carInfo }) => {
  // Lease Applications Store
  const { carData, activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  // Lease Applications Store
  const { emailVerified } = useSelector((state: RootState) => state.auth);

  // Lease Applications Store
  const { device } = useSelector((state: RootState) => state.sequence);

  // History hook
  const navigate = useNavigate();

  // Dispatch hook
  const dispatch = useAppDispatch();

  // Lease Application Storage
  const { accessToken } = useSelector((state: RootState) => state.auth);

  // Confirmation modal control
  const [confirmationVisibility, setConfirmationVisibility] =
    useState<boolean>(false);

  // Cancellation Loading
  const [loading, setLoading] = useState<boolean>(false);

  //Car Details
  const [carDetails, setCarDetails] = useState<CarData | null | undefined>(
    carInfo || carData,
  );

  // Variables

  const isCommercial = activeApplication?.type === Type.B2C;

  const vehicleData = activeApplication?.offer?.vehicle_data;

  const leasingOptions = activeApplication?.offer?.leasing_options;

  const extraMileage = isCommercial
    ? leasingOptions?.finance?.inferior_mileage?.gross
    : leasingOptions?.finance?.inferior_mileage?.net;

  const exceededMileage = isCommercial
    ? leasingOptions?.finance?.exceeding_mileage?.gross
    : leasingOptions?.finance?.exceeding_mileage?.net;

  const showLeasingCancelButton = () =>
    canCancelLeaseApplication(activeApplication?.state) &&
    showCancelButton &&
    emailVerified;
  // Vehicle State
  const getVehicleState = () => {
    if (carDetails?.carVehicleState) {
      return carDetails?.carVehicleState;
    }
    return activeApplication?.vin === '' ? 'NEU' : 'USED';
  };

  // Downpayment
  const getDownPayment = () => {
    if (carDetails?.downPayment !== undefined) {
      return carDetails?.downPayment;
    }
    return isCommercial
      ? leasingOptions?.downPayment?.gross
      : leasingOptions?.downPayment?.net;
  };

  // Format price with german price format
  const { format } = formatGermanPrice();

  // Values
  const {
    image,
    state,
    fuel,
    usedMileage,
    downPayment,
    totalListPrice,
    exceedingMileagePrice,
    inferiorMileagePrice,
    duration,
    mileage,
    additionalCost,
  } = {
    image: carDetails?.defaultImage || vehicleData?.vehicleImageUrl,
    state: getVehicleState(),
    fuel: carDetails?.carFuelType,
    usedMileage: carDetails?.carUsedMileage,
    downPayment: getDownPayment(),
    totalListPrice:
      carDetails?.totalListPrice ||
      toInteger(vehicleData?.emission_data?.totalPrice),
    exceedingMileagePrice: carDetails?.exceedingMileagePrice || exceededMileage,
    inferiorMileagePrice: carDetails?.inferiorMileagePrice || extraMileage,
    duration: carDetails?.duration || leasingOptions?.duration,
    mileage: carDetails?.mileageYear || leasingOptions?.mileage,
    additionalCost: {
      freight:
        carDetails?.additionalCost?.freight ||
        vehicleData?.additional_cost?.freight,
      logistics:
        carDetails?.additionalCost?.logistics ||
        vehicleData?.additional_cost?.logistics,
      registration:
        carDetails?.additionalCost?.registration ||
        vehicleData?.additional_cost?.registration,
      returnFreight:
        carDetails?.additionalCost?.returnFreight ||
        vehicleData?.additional_cost?.returnFreight,
    },
  };

  const carInformation = [
    {
      label: 'Fahrzeugart',
      value: state ? getVehicleType(state) : '-',
    },
    {
      label: 'Beschleunigung',
      value: carData?.carAccelerationToHundredKph
        ? `${format(Number(carData.carAccelerationToHundredKph)).replace(
            '€',
            '',
          )}s (0-100km/h)`
        : '-',
    },
    {
      label: 'Karosserie',
      value: carData?.carBodyType && getVehicleBody(carData?.carBodyType),
    },
    { label: 'Farbe', value: carDetails?.carColorName ?? '-' },
    {
      label: 'Kraftstoff',
      value: CapitalizeFirstLetter(
        (carData?.carFuelType && getFuelType(carData.carFuelType))
          ?.toLowerCase()
          .replace('_', ' ') ?? '-',
      ),
    },
    { label: 'Türen', value: carDetails?.carNumberOfDoors ?? '-' },
    {
      label: 'Getriebe',
      value: CapitalizeFirstLetter(
        (
          carData?.carTransmissionType &&
          getGearBoxType(carData.carTransmissionType)
        )
          ?.toLowerCase()
          .replace('_', ' ') ?? '-',
      ),
    },
    {
      label: 'Effizienzklasse',
      value: carDetails?.efficiencyClass ?? '-',
    },
    {
      label: 'Batterie Kapazität',
      value: carDetails?.batteryCapacity
        ? `${carDetails.batteryCapacity} KWh`
        : '-',
    },
    {
      label: 'Elektrische Reichweite',
      value: carDetails?.electricalRange
        ? `${carDetails.electricalRange} km`
        : '-',
    },
    {
      label: 'Leistung',
      value:
        carDetails?.carPowerKiloWatt && carDetails?.carHorsePower
          ? `${carDetails.carPowerKiloWatt} kW | ${carDetails.carHorsePower} PS`
          : '-',
    },
    {
      label: 'max. Geschwindigkeit',
      value: carDetails?.carMaxSpeed ? `${carDetails.carMaxSpeed} km/h` : '-',
    },
  ];

  // handle flex leasing application
  const { partner, offer } = activeApplication || {};
  const { armada_id: offerId, publication } = offer || {};
  const { publication_id: publicationId, pricing_id: pricingId } =
    publication || {};
  useEffect(() => {
    dispatch(fetchCarDetails({ partner, publicationId, pricingId, offerId }));
  }, [partner, publicationId, pricingId, offerId, dispatch]);

  // handle change and update of the car data
  useEffect(() => {
    setCarDetails(carData);
  }, [carData]);

  // Handle cancel lease
  const cancelLeaseApp = () => {
    setLoading(true);
    if (getIsAllowedToBeCanceled(activeApplication?.state)) {
      cancelLeaseApplication(accessToken, activeApplication?.uuid as string)
        .then(() => {
          dispatch(
            fetchLeaseApp({
              accessToken,
              leaseAppId: activeApplication?.uuid as string,
            }),
          ).then(() => {
            setLoading(false);
            setConfirmationVisibility(false);
            navigate('/');
          });
        })
        .catch(() => {
          setLoading(false);
        });
    }
  };

  return (
    <div className='subheader-body-content container'>
      <div>
        <div>
          {state && (
            <span className='offer content-normal'>
              {state === 'USED'
                ? `Gebrauchtwagen ${usedMileage ? `| ${usedMileage} km` : ''}`
                : 'Neu'}{' '}
              {fuel ? `| ${getFuelType(fuel)}` : ''}
            </span>
          )}
          <br />
          {/* {emissions && (
                <p
                  className='small-text dark-grey consumption'
                  style={{ paddingTop: '24px' }}
                >
                  {co2} g/km (komb.)*, {co2km}/100 km (komb.)* ({emissions})
                </p>
              )} */}
        </div>
        <div className='lease-info'>
          <p className='content-bold'>Vertragsdaten</p>
          <div className='lease-details'>
            {!!totalListPrice && (
              <LeaseRow
                label='Listenpreis'
                value={format(totalListPrice || 0)}
              />
            )}
            {duration && (
              <LeaseRow label='Laufzeit' value={` ${duration} Monate `} />
            )}
            {mileage && (
              <LeaseRow
                label='Laufleistung'
                value={` ${Math.floor(mileage || 0).toLocaleString(
                  'de-DE',
                )} km/Jahr `}
              />
            )}
            {!!exceedingMileagePrice && (
              <LeaseRow
                label='Mehrkilometer-Kosten'
                value={` ${format(exceedingMileagePrice).replace(
                  '€',
                  '',
                )} ct/km `}
              />
            )}
            {!!inferiorMileagePrice && (
              <LeaseRow
                label='Minderkilometer-Erstattung'
                value={` ${format(inferiorMileagePrice).replace(
                  '€',
                  '',
                )} ct/km `}
              />
            )}
          </div>
        </div>
        <div
          className={`lease-info ${carData?.product === 'FLEX' ? 'last' : ''}`}
        >
          <>
            <p className='content-bold'>
              <span>Einmalkosten</span>
            </p>
            <div className='lease-more-details'>
              {!!downPayment && (
                <LeaseRow label='Sonderzahlung' value={format(downPayment)} />
              )}

              {!!additionalCost?.logistics && (
                <LeaseRow
                  label='Logistik'
                  value={format(additionalCost?.logistics)}
                />
              )}

              {!!additionalCost?.returnFreight && (
                <LeaseRow
                  label='Rückholkosten'
                  value={format(additionalCost?.returnFreight)}
                />
              )}

              {!!additionalCost?.registration && (
                <LeaseRow
                  label='Zulassungskosten'
                  value={format(additionalCost?.registration)}
                />
              )}

              {!!additionalCost?.freight && (
                <LeaseRow
                  label={getPartnerText('additionalCost_freight')}
                  value={format(additionalCost?.freight)}
                />
              )}
            </div>
          </>
        </div>
        {carData?.product !== 'FLEX' && (
          <div className='lease-info last'>
            <p className='content-bold'>Monatliche Kosten</p>{' '}
            <div className='lease-details'>
              <LeaseRow
                label='Finanz-Leasingrate'
                value={
                  carData?.services?.finance
                    ? format(carData?.services?.finance)
                    : '-'
                }
              />
              <LeaseRow
                label='Technik-Service'
                value={
                  carData?.services?.technic
                    ? format(carData?.services?.technic)
                    : '-'
                }
              />
              <LeaseRow
                label='Versicherungs-Service'
                value={
                  carData?.services?.insurance
                    ? format(carData?.services?.insurance)
                    : '-'
                }
              />
              <LeaseRow
                label='Fuel & Charge Karte'
                value={
                  carData?.services?.fuelCard
                    ? format(carData?.services?.fuelCard || 0)
                    : '-'
                }
              />
              <LeaseRow
                label='Rückhol-Service'
                value={
                  carData?.services?.returnFreight
                    ? format(carData?.services?.returnFreight || 0)
                    : '-'
                }
              />
              {showLeasingCancelButton() && (
                <div
                  role='button'
                  tabIndex={0}
                  aria-label='Leasingantrag stornieren'
                  className='cancel-application-text content-bold error link focus-style'
                  onClick={() => setConfirmationVisibility(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setConfirmationVisibility(true);
                    }
                  }}
                >
                  {device === EDevice.MOBILE ? (
                    <ContractCancel20 />
                  ) : (
                    <ContractCancel24 />
                  )}
                  {carData?.product !== 'FLEX'
                    ? `Leasingantrag stornieren`
                    : 'Auto-Abo stornieren'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className='car-visual'>
        <div
          className='car-details-image'
          style={{
            backgroundImage: `url(${_.isEmpty(image) ? UnknownCar : image})`,
          }}
        />
        <div className='car-information'>
          {carInformation
            .filter((info) => info.value)
            .map(({ label, value }, index) => (
              <div key={index}>
                <p
                  className='input-text'
                  style={{
                    color: 'var(--vel-color-secondary-400)',
                    fontWeight: 300,
                  }}
                >
                  {label}
                </p>
                <p className='content-bold'>{value}</p>
              </div>
            ))}
        </div>
      </div>
      <ConfirmationModal
        isOpen={confirmationVisibility}
        onClose={() => setConfirmationVisibility(false)}
        confirmationText='Möchtest du deinen Leasingantrag stornieren?'
        cancel={() => setConfirmationVisibility(false)}
        onLoading={loading}
        success={cancelLeaseApp}
      >
        <ul className='arrow-list'>
          <li className='arrow-list-item'>
            <ArrowRight className='arrow-list-icon' />
            <div className='dark-grey content-normal'>
              <div>
                Dein Antrag wird beendet und das Fahrzeug ist nicht mehr für
                dich reserviert.
              </div>
              <div>
                Du wirst zu mobility.ayvens.com weitergeleitet und kannst ein
                anderes Fahrzeug auswählen.
              </div>
            </div>
          </li>
          <li className='arrow-list-item'>
            <ArrowRight className='arrow-list-icon' />
            <p className='dark-grey content-normal'>
              Du kehrst zu deinem Leasingantrag zurück und setzt ihn mit den
              aktualisierten Konditionen fort.
            </p>
          </li>
        </ul>
      </ConfirmationModal>
    </div>
  );
};

export default CarDetails;
